import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Inbox } from "lucide-react";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

export default function BlogIndex() {
  const [items, setItems] = useState(null); // null = loading, [] = empty, [...] = items
  const [error, setError] = useState(null);

  useEffect(() => {
    document.title = "oly Inbox — Blog";
    api
      .get("/marketing/articles")
      .then((r) => setItems(r.data?.items || []))
      .catch((e) => setError(e?.message || "Failed to load articles"));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
      <Header />
      <main className="flex-1" data-testid="blog-index">
        {/* Hero */}
        <section className="border-b border-[#E2E8F0] bg-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-8 py-16 md:py-20">
            <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-[#F26953]">
              <BookOpen className="h-3.5 w-3.5" /> Blog
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0F172A] tracking-tight mt-3">
              Notes on professional email, domains, and small-business setup.
            </h1>
            <p className="text-base md:text-lg text-[#475569] mt-4 max-w-2xl">
              Short, practical guides for getting off <span className="mono text-sm bg-slate-100 px-1.5 py-0.5 rounded">yourname@gmail.com</span> and onto a domain that looks like a real business — without paying for Google Workspace.
            </p>
          </div>
        </section>

        {/* Article grid */}
        <section className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
          {error && (
            <div data-testid="blog-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-4">
              Couldn't load articles: {error}
            </div>
          )}

          {items === null && !error && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl bg-white border border-[#E2E8F0] p-6 animate-pulse">
                  <div className="h-3 w-20 bg-slate-100 rounded mb-4" />
                  <div className="h-6 w-3/4 bg-slate-100 rounded mb-3" />
                  <div className="h-3 w-full bg-slate-100 rounded mb-2" />
                  <div className="h-3 w-5/6 bg-slate-100 rounded" />
                </div>
              ))}
            </div>
          )}

          {Array.isArray(items) && items.length === 0 && (
            <div data-testid="blog-empty" className="text-center py-20">
              <div className="inline-flex h-12 w-12 rounded-full bg-[#FEE7E1] grid place-items-center mb-4">
                <BookOpen className="h-5 w-5 text-[#F26953]" />
              </div>
              <h2 className="text-xl font-bold text-[#0F172A]">First articles coming soon.</h2>
              <p className="text-sm text-[#475569] mt-2 max-w-md mx-auto">
                We publish short, useful guides every week. In the meantime, here's the fastest way to look like a real business:
              </p>
              <Link
                to="/signup"
                data-testid="blog-empty-cta"
                className="mt-6 inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-5 py-3 rounded-lg hover:bg-[#D85340]"
              >
                Set up my email — $3/mo <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}

          {Array.isArray(items) && items.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="blog-grid">
              {items.map((a) => (
                <Link
                  key={a._id || a.slug}
                  to={`/blog/${a.slug}`}
                  data-testid={`blog-card-${a.slug}`}
                  className="group rounded-2xl bg-white border border-[#E2E8F0] p-6 hover:border-[#F26953]/40 hover:shadow-md transition-all"
                >
                  {a.target_keyword && (
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#F26953] mb-3">
                      {a.target_keyword}
                    </div>
                  )}
                  <h2 className="text-xl font-bold text-[#0F172A] leading-snug group-hover:text-[#F26953] transition-colors">
                    {a.title}
                  </h2>
                  {a.meta_description && (
                    <p className="text-sm text-[#475569] mt-2.5 leading-relaxed line-clamp-3">
                      {a.meta_description}
                    </p>
                  )}
                  <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
                    <span>
                      {a.published_at
                        ? new Date(a.published_at).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })
                        : ""}
                      {a.word_count ? ` · ${a.word_count.toLocaleString()} words` : ""}
                    </span>
                    <span className="inline-flex items-center gap-1 text-[#F26953] font-semibold opacity-0 group-hover:opacity-100 transition-opacity">
                      Read <ArrowRight className="h-3 w-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>

        {/* CTA */}
        <section className="bg-[#0F172A] text-white">
          <div className="max-w-5xl mx-auto px-6 lg:px-8 py-16 md:py-20 text-center">
            <Inbox className="h-6 w-6 text-[#F26953] mx-auto" />
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mt-4">
              Ready for a professional email?
            </h2>
            <p className="text-base text-slate-300 mt-3 max-w-xl mx-auto">
              Three minutes from now you could have <span className="mono text-sm bg-white/10 px-1.5 py-0.5 rounded">hello@yourdomain.com</span> forwarded to your existing Gmail.
            </p>
            <Link
              to="/signup"
              data-testid="blog-cta"
              className="mt-6 inline-flex items-center gap-2 bg-[#F26953] text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-[#D85340] shadow-md"
            >
              Set up my email — $3/mo <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
